<template>
  <q-input
    ref="input"
    :model-value="internalValue"
    :disable="disableComputed"
    :readonly="readonlyComputed"
    dense
    flat
    outlined
    hide-bottom-space
    :rules="computedRules"
    :dark="dark"
    :color="color"
    @update:model-value="onUpdateModelValue"
  >
    <template v-if="unit" #append>
      <div class="text-caption">
        {{ unit }}
      </div>
    </template>
    <template v-for="(slot, name) in $slots" v-slot:[name]="scope">
      <slot :name="name" v-bind="scope ?? {}" />
    </template>
    <template v-if="infoButtonId || infoButtonKey" #after>
      <slot name="after" />
      <app-info-button :identifier="infoButtonId" :translation-key="infoButtonKey" />
    </template>
  </q-input>
</template>

<script setup>
import { ref, watch, computed, nextTick, onMounted } from "vue"

import { useModuleDataStore } from "@/stores/moduleData.js"

import { useValidators } from "@/composables/useValidators.js"
import { useFieldsetState } from "@/composables/useFieldsetState.js"

const validators = useValidators()

const moduleStore = useModuleDataStore()

const props = defineProps({
  modelValue: [String, Number, Array],
  unit: String,
  required: Boolean,
  url: Boolean,
  latin: Boolean,
  email: Boolean,
  date: Boolean,
  rules: Array,
  upperCase: Boolean,
  transformUpperCase: Boolean,
  infoButtonId: String,
  infoButtonKey: String,
  requiredCondition: Boolean,
  requiredConditionKey: String,
  disable: Boolean,
  readonly: Boolean,
  formStateInheritanceDisabled: Boolean,
  dark: Boolean
})

const input = ref(null)
const internalValue = ref(props.modelValue)

const { disableComputed, readonlyComputed } = useFieldsetState(props)

const emit = defineEmits(["update:modelValue"])

const color = computed(() => {
  if (props.dark) {
    return "grey-4"
  }
  return null
})

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue
  },
  { immediate: true }
)

const isRequired = computed(() => {
  if (props.required) {
    return true
  }

  if (props.requiredCondition || props.requiredConditionKey) {
    const conditionKey = props.requiredConditionKey || props.infoButtonKey
    return moduleStore.moduleSettings.requiredFields?.includes(conditionKey)
  }

  return false
})

const computedRules = computed(() => {
  const rules = props.rules ? [...props.rules] : []
  if (isRequired.value) {
    rules.push(validators.required)
  }
  if (props.url) {
    rules.push(validators.url)
  }
  if (props.latin) {
    rules.push(validators.latin)
  }
  if (props.email) {
    rules.push(validators.email)
  }
  if (props.date) {
    rules.push(validators.date)
  }
  if (props.upperCase) {
    rules.push(validators.upperCase)
  }

  return rules
})

const onUpdateModelValue = (value) => {
  if (props.transformUpperCase) {
    value = value.toString().toUpperCase()
  }
  internalValue.value = value
  emit("update:modelValue", value)
}

onMounted(() => validators.watchRules(computedRules, input))

defineExpose({
  validate: () => input.value.validate(),
  resetValidation: () => input.value.resetValidation(),
  focus: async () => {
    await nextTick()
    input.value.focus()
  }
})
</script>
